import type { FC } from 'react';
import { useMemo } from 'react';
import { Box, Stack } from '@mui/material';

import { noDataAvailableStyles } from 'src/theme/sharedStyles';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';

import type { NpsQuestionBarProps } from './constants';
import { useNpsQuestionBarSegmentColors } from './hooks';
import NpsQuestionBarSegment from './NpsQuestionBarSegment';

const NpsQuestionBar: FC<NpsQuestionBarProps> = ({
  promoter,
  passive,
  detractor,
  notAnswered,
  isComparison,
}) => {
  const { regular, comparison } = useNpsQuestionBarSegmentColors();
  const colors = useMemo(
    () => (isComparison ? comparison : regular),
    [comparison, regular, isComparison],
  );
  const total = useMemo(
    () => promoter + passive + detractor + notAnswered,
    [promoter, passive, detractor],
  );

  return (
    <Stack
      direction="row"
      sx={{
        height: isComparison ? '1.5rem' : '3.625rem',
        mt: isComparison ? 0.5 : 0,
      }}
    >
      {total <= 0 ? (
        <Box sx={noDataAvailableStyles.noDataAvailable} />
      ) : (
        <>
          <NpsQuestionBarSegment
            percentage={getRelativeValue(detractor, total, 0)}
            absoluteValue={detractor}
            {...colors.detractor}
          />
          <NpsQuestionBarSegment
            percentage={getRelativeValue(passive, total, 0)}
            absoluteValue={passive}
            {...colors.passive}
          />
          <NpsQuestionBarSegment
            percentage={getRelativeValue(promoter, total, 0)}
            absoluteValue={promoter}
            {...colors.promoter}
          />
          <NpsQuestionBarSegment
            percentage={getRelativeValue(notAnswered, total, 0)}
            absoluteValue={notAnswered}
            {...colors.notAnswered}
          />
        </>
      )}
    </Stack>
  );
};

export default NpsQuestionBar;
